import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  useApi,
  useApiProblem,
  type ChannelCloseoutPreview,
  type ChannelCloseoutWriteRequest,
} from '@/shared/api';
import { draftKey } from './channelCloseoutDraft';

const DEBOUNCE_MS = 450;

/**
 * Vorgemerkt wird nur, was der Server geprüft hat. Die Prüfung läuft hier von selbst,
 * sobald die Fassung vollständig ist, und gilt nur für genau diese Fassung: jede Änderung macht
 * das Ergebnis ungültig, bis die neue geprüft ist.
 */
export function useCloseoutPreview(
  propertyGroupId: string,
  draft: ChannelCloseoutWriteRequest,
  ready: boolean,
) {
  const api = useApi();
  const key = draftKey(draft);
  const [checked, setChecked] = useState<{ key: string; result: ChannelCloseoutPreview } | null>(
    null,
  );
  const preview = useMutation({
    mutationFn: (next: ChannelCloseoutWriteRequest) =>
      api.availabilityDistribution.closeouts.preview(propertyGroupId, next),
    onSuccess: (result, next) => setChecked({ key: draftKey(next), result }),
  });
  const { mutate, isPending } = preview;
  // Jede Fassung wird genau einmal geprüft. Scheitert die Prüfung, wartet sie auf eine Änderung
  // oder ein ausdrückliches „Erneut prüfen" — sonst fragte ein Dauerfehler den Server im Takt.
  const [attempted, setAttempted] = useState<string | null>(null);
  useEffect(() => {
    if (!ready || attempted === key || isPending) return;
    const timer = setTimeout(() => {
      setAttempted(key);
      mutate(draft);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [attempted, draft, isPending, key, mutate, ready]);
  const problem = useApiProblem(preview.error);
  const result = checked?.key === key ? checked.result : null;
  const failed = !result && attempted === key && preview.isError;
  return {
    result,
    problem: failed ? problem : null,
    retry: () => setAttempted(null),
  };
}
